/**
 * Guest Identification Component
 *
 * Unified passwordless flow:
 * 1. User enters email
 * 2. A verification code is always sent first
 * 3. User enters code → backend verifies and either logs in (existing) or creates account (new)
 */

import { useState } from 'react';
import { useAuth } from '@/context';
import { authApi } from '@/api/client';
import { Button } from '../shared/Button';
import type { ApiError } from '@/types';
import styles from './GuestIdentification.module.css';

interface Props {
  onAuthenticated: () => void;
}

export function GuestIdentification({ onAuthenticated }: Props) {
  const { isLoggedIn } = useAuth();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  if (isLoggedIn) return null;

  const handleSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setError(null);

    try {
      // Always send a verification code first (works for both new and existing emails)
      await authApi.sendLoginCode(email);
      setMessage('Un code de vérification a été envoyé à votre adresse email.');
      setStep('code');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Erreur lors de l\'envoi du code.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('Le code doit contenir 6 chiffres.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Backend verifies code and either logs in (existing) or creates account (new)
      await authApi.verifyLoginCode(email, code);
      onAuthenticated();
      window.location.reload();
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Code invalide.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setCode('');
    setError(null);
    setMessage(null);
    setIsLoading(true);

    try {
      await authApi.sendLoginCode(email);
      setMessage('Code renvoyé ! Vérifiez votre boîte mail.');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Erreur lors de l\'envoi du code.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>Identification</h3>

      {error && <div className={styles.error}>{error}</div>}
      {message && <div className={styles.success}>{message}</div>}

      {step === 'email' && (
        <form onSubmit={handleSubmitEmail}>
          <div className={styles.field}>
            <label htmlFor="guest-email">Email</label>
            <input
              id="guest-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="votre@email.com"
              required
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            size="small"
            isLoading={isLoading}
            disabled={!email}
          >
            Continuer
          </Button>
        </form>
      )}

      {step === 'code' && (
        <form onSubmit={handleVerifyCode}>
          <p className={styles.info}>
            Un code à 6 chiffres a été envoyé à <strong>{email}</strong>
          </p>

          <div className={styles.field}>
            <label htmlFor="guest-code">Code de vérification</label>
            <input
              id="guest-code"
              type="text"
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              placeholder="000000"
              maxLength={6}
              className={styles.codeInput}
              autoComplete="one-time-code"
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            size="small"
            isLoading={isLoading}
            disabled={code.length !== 6}
          >
            Vérifier le code
          </Button>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.backLink}
              onClick={handleResendCode}
            >
              Renvoyer le code
            </button>
            <button
              type="button"
              className={styles.backLink}
              onClick={() => {
                setStep('email');
                setCode('');
                setError(null);
                setMessage(null);
              }}
            >
              Changer d'email
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
